import {
  createdSubuserSchema,
  daemonSyncSchema,
  hasPermission,
  subuserSchema,
  type Subuser,
} from '@hearth/shared';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Pencil, Plus, ShieldCheck, Trash2, UserRound } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { Confirm } from '@/components/confirm';
import { FormField } from '@/components/form-field';
import {
  PermissionGrid,
  groupsForServer,
  permissionGroupsSchema,
} from '@/components/permission-grid';
import { QueryState } from '@/components/query-state';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ApiRequestError, apiRequest } from '@/lib/api';
import { errorMessage } from '@/lib/forms';
import { serverQuery } from '@/lib/server-queries';

export const Route = createFileRoute('/_app/server/$server/users')({ component: UsersTab });

const usersQuery = (server: string) =>
  queryOptions({
    queryKey: ['server', server, 'users'],
    queryFn: ({ signal }) =>
      apiRequest(`/api/client/servers/${server}/users`, {
        schema: z.array(subuserSchema),
        signal,
      }),
  });

const catalogueQuery = queryOptions({
  queryKey: ['permission-catalogue'],
  queryFn: ({ signal }) =>
    apiRequest('/api/client/permissions', { schema: permissionGroupsSchema, signal }),
  staleTime: Infinity,
});

function UsersTab() {
  const { server: id } = Route.useParams();
  const { data: server } = useSuspenseQuery(serverQuery(id));
  const users = useQuery(usersQuery(id));
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Subuser | 'new' | null>(null);
  const can = (p: Parameters<typeof hasPermission>[1]) => hasPermission(server.permissions, p);

  const remove = useMutation({
    mutationFn: (user: Subuser) =>
      apiRequest(`/api/client/servers/${id}/users/${user.id}`, {
        method: 'DELETE',
        schema: daemonSyncSchema,
      }),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: usersQuery(id).queryKey });
      if (!res.daemonSynced)
        toast.warning(
          `Removed, but the node couldn’t close their open sessions yet: ${res.daemonError}`,
        );
      else toast.success('Access removed.');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-muted-foreground text-sm">
          People you give access to can use this server within the permissions you choose.
        </p>
        {can('user.create') && (
          <Button onClick={() => setEditing('new')}>
            <Plus /> Add user
          </Button>
        )}
      </div>

      <OwnerRow username={server.owner.username} you={server.isOwner} />

      <QueryState query={users}>
        {(list) =>
          list.length === 0 ? (
            <Card className="bg-card/50 border-dashed shadow-none">
              <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
                <span className="bg-elevated text-muted-foreground grid size-11 place-items-center rounded-lg border">
                  <UserRound className="size-6" aria-hidden />
                </span>
                <p className="font-medium">No one else has access</p>
                <p className="text-muted-foreground max-w-sm text-sm">
                  Add a friend or a co-admin by email and choose what they can do.
                </p>
              </CardContent>
            </Card>
          ) : (
            <ul className="grid gap-3">
              {list.map((u) => (
                <li key={u.id}>
                  <Card className="flex-row flex-wrap items-center justify-between gap-3 px-4 py-3">
                    <div className="grid min-w-0 gap-0.5">
                      <p className="flex items-center gap-2 font-medium">
                        <span className="truncate">{u.username}</span>
                        {u.twoFactorEnabled && (
                          <Badge variant="success">
                            <ShieldCheck /> 2FA
                          </Badge>
                        )}
                      </p>
                      <p className="text-muted-foreground truncate text-xs">{u.email}</p>
                      <p className="text-muted-foreground text-xs">
                        {u.permissions.length - 1 <= 0
                          ? 'Console access only'
                          : `${u.permissions.length - 1} permissions`}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {can('user.update') && (
                        <Button variant="outline" size="sm" onClick={() => setEditing(u)}>
                          <Pencil /> Edit
                        </Button>
                      )}
                      {can('user.delete') && (
                        <Confirm
                          title={`Remove ${u.username}?`}
                          description="They lose access immediately, and any console or SFTP session they have open is closed."
                          action="Remove"
                          destructive
                          onConfirm={() => remove.mutate(u)}
                        >
                          <Button variant="outline-destructive" size="sm">
                            <Trash2 /> Remove
                          </Button>
                        </Confirm>
                      )}
                    </div>
                  </Card>
                </li>
              ))}
            </ul>
          )
        }
      </QueryState>

      {editing && (
        <EditDialog
          server={id}
          mine={server.permissions}
          modules={server.profile.modules}
          user={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function EditDialog({
  server,
  mine,
  modules,
  user,
  onClose,
}: {
  server: string;
  mine: string[];
  /** The server's pages (profile modules): page permissions for other games aren't offered. */
  modules: readonly string[];
  user: Subuser | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const catalogue = useQuery(catalogueQuery);
  const [email, setEmail] = useState('');
  const [permissions, setPermissions] = useState<string[]>(
    user?.permissions ?? ['websocket.connect'],
  );
  const [error, setError] = useState<string | undefined>();

  const save = useMutation({
    mutationFn: async (): Promise<{
      daemonSynced?: boolean;
      daemonError?: string | null;
      accountCreated?: boolean;
    }> => {
      if (user)
        return apiRequest(`/api/client/servers/${server}/users/${user.id}`, {
          method: 'PUT',
          body: { permissions },
          schema: subuserSchema.extend(daemonSyncSchema.shape),
        });
      return apiRequest(`/api/client/servers/${server}/users`, {
        method: 'POST',
        body: { email, permissions },
        schema: createdSubuserSchema,
      });
    },
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: usersQuery(server).queryKey });
      if (res.daemonSynced === false)
        toast.warning(
          `Saved, but the node couldn’t close their open sessions yet: ${res.daemonError}`,
        );
      else if (res.accountCreated)
        toast.success('Invited. They’ll get an email to set their password.');
      else toast.success(user ? 'Permissions updated.' : 'Access granted.');
      onClose();
    },
    onError: (e) => {
      const field =
        e instanceof ApiRequestError
          ? (e.fieldErrors('body.email')[0] ?? e.fieldErrors('body.permissions')[0])
          : undefined;
      if (field) setError(field);
      else toast.error(errorMessage(e));
    },
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{user ? `Permissions for ${user.username}` : 'Add a user'}</DialogTitle>
          <DialogDescription>
            {user
              ? 'Changes apply right away; their open sessions are closed so they pick up the new list.'
              : 'If they don’t have an account yet, one is created and they get an email to set a password.'}
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(undefined);
            save.mutate();
          }}
        >
          {!user && (
            <FormField label="Email address" error={error}>
              <Input
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(undefined);
                }}
              />
            </FormField>
          )}
          {user && error && (
            <p className="text-destructive text-sm" role="alert">
              {error}
            </p>
          )}
          <QueryState query={catalogue}>
            {(groups) => (
              <PermissionGrid
                groups={groupsForServer(groups, modules, user?.permissions ?? [])}
                value={permissions}
                onChange={setPermissions}
                mine={mine}
                disabled={save.isPending}
              />
            )}
          </QueryState>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending || (!user && !email)}>
              {save.isPending ? 'Saving…' : user ? 'Save permissions' : 'Add user'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** The owner always has full access and isn't a subuser, so it is shown separately. */
function OwnerRow({ username, you }: { username: string; you: boolean }) {
  return (
    <Card className="flex-row items-center gap-3 px-4 py-3">
      <span
        aria-hidden
        className="bg-primary/12 text-primary grid size-9 place-items-center rounded-full text-sm font-semibold uppercase"
      >
        {username.slice(0, 2)}
      </span>
      <div className="grid min-w-0 gap-0.5">
        <p className="flex items-center gap-2 font-medium">
          <span className="truncate">{username}</span>
          {you && <Badge variant="secondary">You</Badge>}
          <Badge variant="info">Owner</Badge>
        </p>
        <p className="text-muted-foreground text-xs">Full access to everything on this server</p>
      </div>
    </Card>
  );
}
